import * as THREE from "three";
import { Raycaster, Vector2, Vector3 } from "three";
import { GPUPicker } from "three_gpu_picking";
import AppData from "../../../app.data";
import { Utils } from "../../../utils/utils";
import Renderer from "../../../renderer/renderer";
import ArtworkLoader from "../../../loaders/artwork/artwork.loader";
import { EventArtworkManipulator } from "./artwork.handler";
import { EventMediaHandler } from "./media.handler";
import type EventService from "../event.service";
import { ArtworkWrapper } from "@/types/artworksData";
import type { RaycastSource, ResolvedRaycastTarget } from "@/components/3d/renderer/managers/vr.types";
import { filterTargetsForRaycaster } from "./raycast.utils";

const CLICK_THRESHOLD_DISTANCE = 15;
const POINTS_RAYCAST_THRESHOLD = 0.08;
type PointerActivation = "all" | "mobile-single-tap" | "mobile-double-tap";

export class EventRaycaster {
    private appData: AppData;
    private utils: Utils;
    private renderer: Renderer;
    private canvas: HTMLCanvasElement;
    private artworkLoaderService: ArtworkLoader;
    private artworkHandler: EventArtworkManipulator;
    private mediaHandler: EventMediaHandler;
    private eventService: EventService;

    private gpuPicker: GPUPicker | null = null;
    private gpuPickerSceneUUID: string | null = null;
    private gpuPickerLastObjectId: number | null = null;
    private gpuPickerLastObject: THREE.Object3D | null = null;
    private currentHoveredSlot: THREE.Object3D | null = null;

    constructor(
        eventService: EventService,
        renderer: Renderer,
        canvas: HTMLCanvasElement,
        artworkLoader: ArtworkLoader,
        artworkHandler: EventArtworkManipulator,
        mediaHandler: EventMediaHandler
    ) {
        this.eventService = eventService;
        this.renderer = renderer;
        this.canvas = canvas;
        this.artworkLoaderService = artworkLoader;
        this.artworkHandler = artworkHandler;
        this.mediaHandler = mediaHandler;
        this.appData = new AppData();
        this.utils = new Utils();
    }

    public slotClickHandler = (event, activation: PointerActivation = "all") => {
        event.preventDefault();
        if (!this.appData.camera) return
        const raycaster = new Raycaster();
        raycaster.params.Points.threshold = POINTS_RAYCAST_THRESHOLD;
        const pointer = new Vector2();
        const clientX = event.clientX ? event.clientX : (event.changedTouches && event.changedTouches[0]) ? event.changedTouches[0].clientX : null;
        const clientY = event.clientY ? event.clientY : (event.changedTouches && event.changedTouches[0]) ? event.changedTouches[0].clientY : null;
        var rect = this.canvas.getBoundingClientRect();
        pointer.x = ((clientX - rect.left) / (rect.right - rect.left)) * 2 - 1;
        pointer.y = -((clientY - rect.top) / (rect.bottom - rect.top)) * 2 + 1;
        raycaster.setFromCamera(pointer, this.appData.camera);

        // merge children of boths scenes and filter to only raycast agains needed objects so we can handle it in a single thread
        const faltenedChildren = this.gatherMeshesForRaycast()

        const intersects = this.safeIntersectObjects(raycaster, faltenedChildren);
        const resolvedTarget = this.resolveTargetFromIntersects(intersects);
        if (resolvedTarget) {
            this.dispatchResolvedTarget(resolvedTarget, "pointer", activation);
        }

    };

    public mouseOverArtworksHandler = (event: MouseEvent) => {
        //prevent firing when keys are being pressed
        if (Object.keys(this.appData.keyStrokes).length > 0 || !this.appData.camera) return;

        window.document.body.style.cursor = "default";
        event.preventDefault();

        const gpuPicked = this.getObjectFromGpuPick(event);
        if (gpuPicked) {
            const slotParent = this.findSlotParent(gpuPicked);
            this.updateHoverVisuals(slotParent);
            return;
        }

        const fallbackPicked = this.getObjectFromPointerRaycast(event);
        const slotParent = this.findSlotParent(fallbackPicked);
        this.updateHoverVisuals(slotParent);
    }

    private updateHoverVisuals(slotParent: THREE.Object3D | null) {
        window.document.body.style.cursor = slotParent ? "pointer" : "default";
        if (slotParent === this.currentHoveredSlot) {
            return;
        }
        this.currentHoveredSlot = slotParent;
        this.renderer.setOutlinedObject(slotParent);
    }

    private findSlotParent(object: THREE.Object3D | null): THREE.Object3D | null {
        let slotParent: THREE.Object3D | null = object;
        while (slotParent && (!slotParent.name || !slotParent.name.startsWith("Slot-"))) {
            slotParent = slotParent.parent;
        }
        return slotParent || null;
    }

    private getObjectFromGpuPick(event: MouseEvent): THREE.Object3D | null | undefined {
        if (!this.ensureGpuPicker() || !this.gpuPicker) {
            return undefined;
        }

        const rect = this.canvas.getBoundingClientRect();
        const pixelRatio = window.devicePixelRatio || 1;
        const localX = (event.clientX - rect.left) * pixelRatio;
        const localY = (event.clientY - rect.top) * pixelRatio;

        const rendererDom = this.renderer?.renderer?.domElement;
        if (!rendererDom) {
            return undefined;
        }

        if (localX < 0 || localY < 0 || localX > rendererDom.width || localY > rendererDom.height) {
            this.gpuPickerLastObjectId = null;
            this.gpuPickerLastObject = null;
            return null;
        }

        const pickedId = this.gpuPicker.pick(Math.floor(localX), Math.floor(localY), this.shouldPickGpuObject);
        if (!pickedId || pickedId <= 0) {
            this.gpuPickerLastObjectId = null;
            this.gpuPickerLastObject = null;
            return null;
        }

        if (pickedId === this.gpuPickerLastObjectId) {
            return this.gpuPickerLastObject;
        }

        const pickedObject = this.appData.spaceScene.getObjectById(pickedId) || null;
        this.gpuPickerLastObjectId = pickedId;
        this.gpuPickerLastObject = pickedObject;
        return pickedObject;
    }

    private shouldPickGpuObject = (object: THREE.Object3D) => {
        if (!object.visible) return false;
        if ((object as THREE.Mesh).isMesh && !(object as THREE.Mesh).geometry) {
            return false;
        }
        if ((object as THREE.Points).isPoints && !(object as THREE.Points).geometry) {
            return false;
        }

        const name = object.name || "";
        if (
            name.includes("octree-mesh") ||
            name.includes("entryTo_Space") ||
            name.includes("cover_Space-") ||
            name.includes("remove_Space-") ||
            name.includes("sky") ||
            name.includes("sceneHolder") ||
            name.includes("TransformControls")
        ) {
            return false;
        }

        if (this.artworkHandler.isArtowrkSelectToggle) {
            return this.isArtworkSelectionCandidate(object);
        }

        const data = object.userData || {};
        if (data.artworkWrapper || data.owner || data.isActionToNextSpace || data.isActionToRemoveSpace || data.isSprite) {
            return true;
        }

        return object.type === "Mesh" || object.type === "Points";
    }

    private ensureGpuPicker() {
        if (!this.renderer || !this.renderer.renderer || !this.appData.spaceScene || !this.appData.camera) {
            return false;
        }

        if (this.renderer.renderer.xr?.isPresenting) {
            return false;
        }

        const sceneUUID = this.appData.spaceScene.uuid;
        if (this.gpuPicker && this.gpuPickerSceneUUID !== sceneUUID) {
            this.gpuPicker = null;
            this.gpuPickerSceneUUID = null;
            this.gpuPickerLastObject = null;
            this.gpuPickerLastObjectId = null;
        }

        if (!this.gpuPicker) {
            try {
                this.gpuPicker = new GPUPicker(THREE, this.renderer.renderer, this.appData.spaceScene, this.appData.camera);
                this.gpuPickerSceneUUID = sceneUUID;
            } catch (error) {
                console.warn("Failed to initialize GPU picker", error);
                this.gpuPicker = null;
                this.gpuPickerSceneUUID = null;
                return false;
            }
        }

        return true;
    }

    private gatherMeshesForRaycast() {
        if (!this.appData.artworksScene || !this.appData.spaceScene) {
            return []
        }
        const faltenedChildren = []

        if (this.artworkHandler.isArtowrkSelectToggle) {

            // we only need to raycast on artworks now
            this.appData.artworksScene.traverse((child) => {
                if (this.isArtworkSelectionCandidate(child)) {
                    faltenedChildren.push(child)
                }
            })
        } else {
            this.appData.artworksScene.traverse((child) => {
                if (child.type === "Mesh" || child.type === "Points") {
                    faltenedChildren.push(child)
                } else if (child.type === "Sprite" && child.userData.isSprite) {
                    faltenedChildren.push(child)
                }
            })

            this.appData.spaceScene.traverse((child) => {
                if (child.type === "Mesh") {
                    if (child.name.includes("entryTo_") || child.name.includes("remove_") || child.name.includes("remove_")) {
                        faltenedChildren.push(child)
                    }
                }
            })
        }
        const spaceObject = this.appData.spaceScene.getObjectByName("sceneHolder")
        if (spaceObject) {
            spaceObject.traverse((child) => {
                if (child.type === "Mesh" &&
                    !child.name.includes("octree-mesh") &&
                    !child.name.includes("entryTo_Space") &&
                    !child.name.includes("cover_Space-") &&
                    !child.name.includes("remove_Space-") &&
                    !child.name.includes("sky") &&
                    !child.name.includes("sceneHolder")
                ) {
                    faltenedChildren.push(child)
                }
            })
        }

        return faltenedChildren.filter((child) => this.isSafeRaycastTarget(child))
    }

    private isSafeRaycastTarget(object: THREE.Object3D | null | undefined) {
        if (!object) return false;
        if (!object.visible) return false;
        if (!object.parent) return false;
        if (!(object as any).matrixWorld) return false;
        if ((object as THREE.Mesh).isMesh && !(object as THREE.Mesh).geometry) return false;
        if ((object as THREE.Points).isPoints && !(object as THREE.Points).geometry) return false;
        return true;
    }

    private safeIntersectObjects(raycaster: Raycaster, targets: THREE.Object3D[]) {
        try {
            const safeTargets = filterTargetsForRaycaster(raycaster, targets);
            return raycaster.intersectObjects(safeTargets, false);
        } catch (error) {
            console.warn("EventRaycaster: failed to raycast scene targets", error);
            return [];
        }
    }

    private getObjectFromPointerRaycast(event: MouseEvent): THREE.Object3D | null {
        if (!this.appData.camera) {
            return null;
        }

        const raycaster = new Raycaster();
        raycaster.params.Points.threshold = POINTS_RAYCAST_THRESHOLD;

        const pointer = new Vector2();
        const rect = this.canvas.getBoundingClientRect();
        pointer.x = ((event.clientX - rect.left) / (rect.right - rect.left)) * 2 - 1;
        pointer.y = -((event.clientY - rect.top) / (rect.bottom - rect.top)) * 2 + 1;
        raycaster.setFromCamera(pointer, this.appData.camera);

        const intersects = this.safeIntersectObjects(raycaster, this.gatherMeshesForRaycast());
        for (let index = 0; index < intersects.length; index++) {
            const intersect = intersects[index];
            if (!intersect?.object || intersect.distance >= CLICK_THRESHOLD_DISTANCE) {
                continue;
            }

            let currentObj: THREE.Object3D | null = intersect.object;
            let isParentChainVisible = true;
            while (currentObj) {
                if (!currentObj.visible) {
                    isParentChainVisible = false;
                    break;
                }
                currentObj = currentObj.parent;
            }

            if (isParentChainVisible) {
                return intersect.object;
            }
        }

        return null;
    }

    public resolveTargetFromRay(origin: THREE.Vector3, direction: THREE.Vector3): ResolvedRaycastTarget | null {
        const raycaster = new Raycaster(origin, direction.clone().normalize());
        raycaster.params.Points.threshold = POINTS_RAYCAST_THRESHOLD;
        const intersects = this.safeIntersectObjects(raycaster, this.gatherMeshesForRaycast());
        return this.resolveTargetFromIntersects(intersects);
    }

    public dispatchResolvedTarget(
        target: ResolvedRaycastTarget | null,
        _source: RaycastSource,
        activation: PointerActivation = "all"
    ) {
        if (!target?.object?.userData) {
            return;
        }
        this.handleRaycastIntersections(target.object, activation);
    }

    private resolveTargetFromIntersects(intersects: THREE.Intersection[]): ResolvedRaycastTarget | null {
        for (let index = 0; index < intersects.length; index++) {
            const intersect = intersects[index];
            if (!intersect?.object || intersect.distance >= CLICK_THRESHOLD_DISTANCE) {
                continue;
            }

            let currentObj: THREE.Object3D | null = intersect.object;
            let isParentChainVisible = true;
            while (currentObj) {
                if (!currentObj.visible) {
                    isParentChainVisible = false;
                    break;
                }
                currentObj = currentObj.parent;
            }

            if (!isParentChainVisible || !intersect.object.userData) {
                continue;
            }

            const data = intersect.object.userData;
            if (data.isActionToNextSpace || data.isActionToRemoveSpace) {
                return { kind: "space-edit", object: intersect.object, point: intersect.point.clone(), distance: intersect.distance };
            }
            if (data.isSprite) {
                return { kind: "diorama-sprite", object: intersect.object, point: intersect.point.clone(), distance: intersect.distance, artworkWrapper: data as ArtworkWrapper };
            }
            if (!data.artworkWrapper || data.artworkWrapper.artworks?.length === 0) {
                if (!Number.isFinite(Number(data.index))) {
                    continue;
                }
                return { kind: "empty-slot", object: intersect.object, point: intersect.point.clone(), distance: intersect.distance, artworkWrapper: data.artworkWrapper || null };
            }

            const selectedArtwork = data.artworkWrapper.artworks[data.artworkWrapper.activeArtwork];
            if (!selectedArtwork) {
                return null;
            }

            if (selectedArtwork.type === "diorama") {
                return { kind: "diorama", object: intersect.object, point: intersect.point.clone(), distance: intersect.distance, artworkWrapper: data.artworkWrapper };
            }
            if (selectedArtwork.type === "sound") {
                return { kind: "sound", object: intersect.object, point: intersect.point.clone(), distance: intersect.distance, artworkWrapper: data.artworkWrapper };
            }
            return { kind: "artwork", object: intersect.object, point: intersect.point.clone(), distance: intersect.distance, artworkWrapper: data.artworkWrapper };
        }

        return null;
    }

    public getRotatableArtworkFromEvent(event: MouseEvent | TouchEvent): THREE.Object3D | null {
        const pointerEvent = this.getPointerLikeEvent(event);
        if (!pointerEvent) return null;

        const pickedObject = this.getObjectFromPointerRaycast(pointerEvent as MouseEvent);
        if (!pickedObject || !this.isArtworkSelectionCandidate(pickedObject)) return null;

        const slotParent = this.findSlotParent(pickedObject);
        const selectedRoot = slotParent
            ? this.getDirectSlotChild(slotParent, pickedObject) || pickedObject
            : pickedObject;
        const wrapper = pickedObject.userData?.artworkWrapper || selectedRoot.userData?.artworkWrapper;
        if (!wrapper?.artworks?.length) return null;
        return selectedRoot;
    }

    private getDirectSlotChild(slotParent: THREE.Object3D, object: THREE.Object3D) {
        let current: THREE.Object3D | null = object;
        while (current?.parent && current.parent !== slotParent) {
            current = current.parent;
        }
        return current?.parent === slotParent ? current : null;
    }

    private getPointerLikeEvent(event: MouseEvent | TouchEvent): MouseEvent | { clientX: number; clientY: number } | null {
        const touchEvent = event as TouchEvent;
        const touch = touchEvent.touches?.[0] || touchEvent.changedTouches?.[0];
        if (touch) {
            return { clientX: touch.clientX, clientY: touch.clientY };
        }
        const mouseEvent = event as MouseEvent;
        if (typeof mouseEvent.clientX === "number" && typeof mouseEvent.clientY === "number") {
            return mouseEvent;
        }
        return null;
    }

    private isArtworkSelectionCandidate(object: THREE.Object3D) {
        return (
            (object.type === "Mesh" || object.type === "Points") &&
            typeof object.userData?.index === "number" &&
            object.userData.index > -1
        );
    }

    private handleRaycastIntersections(INTERSECTED, activation: PointerActivation = "all") {
        const singleMobileTap = activation === "mobile-single-tap";
        const doubleMobileTap = activation === "mobile-double-tap";

        if (doubleMobileTap && this.artworkHandler.isArtowrkSelectToggle) {
            return;
        }
        if (this.artworkHandler.isArtowrkSelectToggle) {
            this.artworkHandler.moveArtwork(INTERSECTED)
            return
        }

        const intersectedData = INTERSECTED.userData

        if (intersectedData.isActionToNextSpace) {
            if (!doubleMobileTap) {
                this.eventService.onSpaceEditClicked && this.eventService.onSpaceEditClicked({ spaceNumber: intersectedData.makeVisibleSpaceID, action: "add" })
            }
            return
        }

        if (intersectedData.isActionToRemoveSpace) {
            if (!doubleMobileTap) {
                this.eventService.onSpaceEditClicked && this.eventService.onSpaceEditClicked({ spaceNumber: intersectedData.spaceToRemoveID, action: "remove" })
            }
            return
        }

        if (intersectedData.isSprite) {
            if (!doubleMobileTap) {
                this.onDioramaArrowsClicked(intersectedData, intersectedData.slotTarget)
            }
            return
        }

        if (!intersectedData.artworkWrapper ||
            (intersectedData.artworkWrapper.artworks &&
                intersectedData.artworkWrapper.artworks.length === 0)) {
            // empty slot
            if (!doubleMobileTap) {
                this.eventService.onArtworkClicked && this.eventService.onArtworkClicked(intersectedData)
            }
            return
        }

        const selectedArtwork = intersectedData.artworkWrapper.artworks[intersectedData.artworkWrapper.activeArtwork]
        if (!selectedArtwork) return


        switch (selectedArtwork.type) {
            case "diorama":
                if (this.appData.spaceData.subtype === "pseudoMoM") {
                    if (!singleMobileTap) {
                        this.eventService.onArtworkClicked && this.eventService.onArtworkClicked(intersectedData)
                    }
                } else if (!doubleMobileTap) {
                    this.eventService.onUserDioramaClicked && this.eventService.onUserDioramaClicked(intersectedData)
                }
                break
            case "sound":
                if (this.appData.galleryData.editable) {
                    if (!singleMobileTap) {
                        this.eventService.onArtworkClicked && this.eventService.onArtworkClicked(intersectedData)
                    }
                } else if (!doubleMobileTap) {
                    this.mediaHandler.handleSoundClick(intersectedData)
                }
                break
            case "3dmodel":
                if (!singleMobileTap) {
                    this.eventService.onArtworkClicked && this.eventService.onArtworkClicked(intersectedData)
                }
                break
            case "image":
                if (!singleMobileTap) {
                    this.eventService.onArtworkClicked && this.eventService.onArtworkClicked(intersectedData)
                }
                break
            case "video":
                if (!singleMobileTap) {
                    this.eventService.onArtworkClicked && this.eventService.onArtworkClicked(intersectedData)
                }
                break
        }
    }

    private onDioramaArrowsClicked(artworkWrapper: ArtworkWrapper, slotId: number) {
        // Use the canonical wrapper from appData when available to avoid stale copies
        const canonicalWrapper = (this.appData.galleryData?.artworkWrappers?.find(wrapper => wrapper.slotTarget === slotId)) || artworkWrapper;

        let nextValue = null
        let activeArtwork = canonicalWrapper.activeArtwork
        let artWorksLength = canonicalWrapper.artworks.length
        // @ts-ignore
        if (artworkWrapper.isUp) {
            nextValue = (activeArtwork + 1) % artWorksLength;
            // @ts-ignore
        } else if (artworkWrapper.isDown) {
            nextValue = (activeArtwork - 1 + artWorksLength) % artWorksLength;
        }
        canonicalWrapper.activeArtwork = nextValue
        this.artworkLoaderService.loadArtworkInSlot(canonicalWrapper, slotId).then(res => {
            if (this.appData.spaceData?.subtype === "pseudoMoM") {
                this.renderer.refreshPseudoMoMLightForSlot(slotId)
            }
            this.renderer.addToRenderQueue()
        })
    }
}

